import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

export interface MeUser {
  id: string;
  email: string;
  name: string | null;
  admin: 'superadmin' | 'support' | 'finance' | null;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<MeUser>('/auth/me');
      } catch {
        return null;
      }
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<MeUser>('/auth/login', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export interface AdminMetrics {
  mrr_cents: number;
  arr_cents: number;
  active_subscriptions: number;
  canceled_subscriptions: number;
  users: { total: number; new_30d: number; active_30d: number };
  workspaces_total: number;
  ai: {
    month_cost_usd: string;
    day_cost_usd: string;
    requests: number;
    tokens_in: number;
    tokens_out: number;
    top_models: Array<{ provider: string | null; model: string | null; cost: string }>;
  };
  payments: { revenue_brl_cents: number; approved: number; rejected: number };
}

export function useAdminMetrics() {
  return useQuery({ queryKey: ['admin', 'metrics'], queryFn: () => api<AdminMetrics>('/admin/metrics') });
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  bannedAt: string | null;
  banReason: string | null;
  adminRole: 'superadmin' | 'support' | 'finance' | null;
}

export function useAdminUsers(q?: string) {
  return useQuery({
    queryKey: ['admin', 'users', q ?? ''],
    queryFn: () => api<{ users: AdminUser[] }>('/admin/users' + (q ? `?q=${encodeURIComponent(q)}` : '')),
  });
}

export interface UserDetailsResponse {
  user: AdminUser;
  workspaces: Array<{ workspaceId: string; role: string; name?: string }>;
  subscriptions: Array<{
    workspaceId: string;
    status: string;
    currentPeriodEnd: string | null;
    planVersionId: string;
    planSlug?: string;
    planName?: string;
  }>;
}

export function useAdminUserDetails(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => api<UserDetailsResponse>(`/admin/users/${id}`),
    enabled: !!id,
  });
}

export function useBanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; banned: boolean; reason?: string }) =>
      api(`/admin/users/${input.id}`, {
        method: 'PATCH',
        body: { banned: input.banned, banReason: input.reason },
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', variables.id] });
    },
  });
}

export function useAssignManualSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; workspaceId: string; planSlug: string; expiresAt?: string | null }) =>
      api(`/admin/users/${input.userId}/workspaces/${input.workspaceId}/subscription`, {
        method: 'POST',
        body: { planSlug: input.planSlug, expiresAt: input.expiresAt },
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

export function useImpersonate() {
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      api<{ impersonating: { id: string; email: string; name: string | null }; expiresInSec: number }>(
        `/admin/users/${input.id}/impersonate`,
        { method: 'POST', body: { reason: input.reason } },
      ),
  });
}

export interface AdminWorkspace {
  id: string;
  slug: string;
  name: string;
  ownerEmail: string | null;
  createdAt: string;
  subStatus: string | null;
}

export function useAdminWorkspaces(q?: string) {
  return useQuery({
    queryKey: ['admin', 'workspaces', q ?? ''],
    queryFn: () =>
      api<{ workspaces: AdminWorkspace[] }>(
        '/admin/workspaces' + (q ? `?q=${encodeURIComponent(q)}` : ''),
      ),
  });
}

export interface WorkspaceDetailsResponse {
  workspace: {
    id: string;
    slug: string;
    name: string;
    ownerId: string;
    ownerEmail: string | null;
    createdAt: string;
  };
  subscription: {
    workspaceId: string;
    status: string;
    currentPeriodEnd: string | null;
    lockedPriceBrl: number;
    planSlug: string;
  } | null;
}

export function useAdminWorkspace(workspaceId: string | null) {
  return useQuery({
    queryKey: ['admin', 'workspace', workspaceId],
    queryFn: () =>
      workspaceId
        ? api<WorkspaceDetailsResponse>(`/admin/workspaces/${workspaceId}`)
        : Promise.resolve(null),
    enabled: !!workspaceId,
  });
}

export function useUpdateWorkspaceSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workspaceId: string;
      planSlug: string;
      expiresAt: string | null;
    }) =>
      api<{ ok: boolean }>(`/admin/workspaces/${input.workspaceId}/subscription`, {
        method: 'POST',
        body: {
          planSlug: input.planSlug,
          expiresAt: input.expiresAt,
        },
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'workspaces'] });
      qc.invalidateQueries({ queryKey: ['admin', 'workspace', variables.workspaceId] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] }); // invalidates users just in case
    },
  });
}

export interface AdminPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  priceBrl: number;
  priceUsd: number | null;
  trialDays: number | null;
  trialRequiresCard: boolean;
  features: Record<string, unknown>;
  aiLimits: Record<string, unknown>;
  isActive: boolean;
  isPublic: boolean;
}

export function useAdminPlans() {
  return useQuery({ queryKey: ['admin', 'plans'], queryFn: () => api<{ plans: AdminPlan[] }>('/admin/plans') });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      slug: string;
      name: string;
      description?: string | null;
      price_brl: number;
      price_usd: number | null;
      trial_days: number | null;
      trial_requires_card: boolean;
      features: Record<string, unknown>;
      ai_limits: Record<string, unknown>;
      is_active: boolean;
      is_public: boolean;
    }) =>
      api(`/admin/plans/${input.slug}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'plans'] }),
  });
}

export function useApplyPlanLatest() {
  return useMutation({
    mutationFn: (slug: string) =>
      api<{ ok: boolean; updated: number; version: number }>(`/admin/plans/${slug}/apply-latest`, {
        method: 'POST',
      }),
  });
}

export interface Integration {
  slug: string;
  label: string;
  configured: boolean;
  metadata?: Record<string, unknown> | null;
  updatedAt?: string;
}

export function useIntegrations() {
  return useQuery({
    queryKey: ['admin', 'integrations'],
    queryFn: () => api<{ integrations: Integration[] }>('/admin/integrations'),
  });
}

export function useUpsertIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; label: string; secret: Record<string, string> }) =>
      api(`/admin/integrations/${input.slug}`, {
        method: 'PUT',
        body: { label: input.label, secret: input.secret },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'integrations'] }),
  });
}

export interface AIPromptVersion {
  id: string;
  slug: string;
  version: number;
  body: string;
  isActive: boolean;
  createdAt: string;
}

export function usePromptList() {
  return useQuery({
    queryKey: ['admin', 'prompts'],
    queryFn: () => api<{ prompts: AIPromptVersion[] }>('/admin/ai/prompts'),
  });
}

export function useCreatePromptVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; body: string }) =>
      api<AIPromptVersion>('/admin/ai/prompts', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'prompts'] }),
  });
}

export function useActivatePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      api(`/admin/ai/prompts/${input.id}/activate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'prompts'] }),
  });
}

export interface FeatureFlag {
  id: string;
  key: string;
  enabledGlobally: boolean;
  enabledPlans: string[];
  enabledWorkspaces: string[];
  rolloutPercentage: number | null;
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['admin', 'flags'],
    queryFn: () => api<{ flags: FeatureFlag[] }>('/admin/feature-flags'),
  });
}

export function useUpsertFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; enabledGlobally: boolean }) =>
      api(`/admin/feature-flags/${input.key}`, {
        method: 'PUT',
        body: {
          enabledGlobally: input.enabledGlobally,
          enabledPlans: [],
          enabledWorkspaces: [],
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'flags'] }),
  });
}

export interface AuditEvent {
  id: string;
  workspaceId: string | null;
  userId: string | null;
  actorType: 'user' | 'admin' | 'system';
  action: string;
  targetType: string | null;
  targetId: string | null;
  meta: unknown;
  ip: string | null;
  createdAt: string;
}

export function useAuditLog(filters?: { action?: string }) {
  const qs = filters?.action ? `?action=${encodeURIComponent(filters.action)}` : '';
  return useQuery({
    queryKey: ['admin', 'audit', filters?.action ?? ''],
    queryFn: () => api<{ events: AuditEvent[] }>('/admin/audit-log' + qs),
  });
}

export interface AdminPayment {
  id: string;
  workspaceId: string;
  mpPaymentId: string;
  amount: number;
  currency: string;
  method: string | null;
  status: string;
  paidAt: string | null;
  createdAt: string;
}

export function useAdminPayments() {
  return useQuery({
    queryKey: ['admin', 'payments'],
    queryFn: () => api<{ payments: AdminPayment[] }>('/admin/payments'),
  });
}

export interface TwoFAStatus {
  enrolled: boolean;
  verified: boolean;
  role: 'superadmin' | 'support' | 'finance';
}

export function useTwoFAStatus() {
  return useQuery({ queryKey: ['admin', '2fa'], queryFn: () => api<TwoFAStatus>('/admin/2fa/status') });
}

export function useEnroll2FA() {
  return useMutation({
    mutationFn: () =>
      api<{ secret: string; otpauthUrl: string; qrDataUrl: string }>('/admin/2fa/enroll', {
        method: 'POST',
      }),
  });
}

export function useVerify2FA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string }) =>
      api<{ verified: boolean }>('/admin/2fa/verify', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', '2fa'] }),
  });
}
